import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LocationService } from '../services/location.service';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { MessageTranslateService } from '../../../shared/services/message.service';
import * as locationActions from './location.actions';
import * as operatorsSelectors from '../../../store/operators/operators.selectors';
import * as operatorsActions from '../../../store/operators/operators.actions';
import { concatLatestFrom } from '@ngrx/operators';
import {
  selectCurrentFilter,
  selectCurrentPagination,
} from './location.selectors';
import { LocationRequestUtil } from '../utils/location-request.util';
import { catchError, map, of, switchMap, tap } from 'rxjs';

export class LocationEffects {
  actions$: Actions = inject(Actions);
  locationService: LocationService = inject(LocationService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);

  loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        locationActions.loadLocationList,
        locationActions.saveLocationSuccess,
        locationActions.updateLocationFilterSuccess,
        locationActions.changePaginationSuccessful,
        operatorsActions.SetCurrentOperatorSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(selectCurrentFilter),
        this.store.select(selectCurrentPagination),
      ]),
      switchMap(([_action, selectedOperator, currentFilter, pagination]) =>
        this.locationService
          .searchLocations(
            LocationRequestUtil.createLocationListRequest(
              selectedOperator!.id!,
              pagination,
              currentFilter
            )
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return locationActions.loadLocationListSuccess({
                  locationsResponse: res.value!,
                });
              } else {
                return locationActions.loadLocationListFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                locationActions.loadLocationListFailure({
                  error: err.message || 'Loading locations failed',
                })
              )
            )
          )
      )
    )
  );

  saveLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.saveLocation),
      switchMap((requestData) =>
        this.locationService.createLocation(requestData.location).pipe(
          map((res) => {
            if (res.isSuccess) {
              return locationActions.saveLocationSuccess();
            } else {
              return locationActions.saveLocationFailure({ error: res.error! });
            }
          }),
          catchError((err) =>
            of(
              locationActions.saveLocationFailure({
                error: err.message || 'Saving location failed',
              })
            )
          )
        )
      )
    )
  );

    loadFilterParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.loadLocationFilterParameters),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator)
      ]),
      switchMap(([_action, operator]) =>
        this.locationService.getFilterParameters(operator!.id!).pipe(
          map((res) => {
            if (res.isSuccess) {
              return locationActions.loadLocationFilterParametersSuccess({filterParameters: res.value!});
            } else {
              return locationActions.loadLocationFilterParametersFailure({ error: res.error! });
            }
          }),
          catchError((err) =>
            of(
              locationActions.loadLocationFilterParametersFailure({
                error: err.message || 'Load location filter parameters failed',
              })
            )
          )
        )
      )
    )
  );

  updateLocationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.updateLocationStatus),
      switchMap(({ locationId, status }) =>
        this.locationService.updateStatus(locationId, status).pipe(
          map((res) => {
            if (res.isSuccess) {
              return locationActions.updateLocationStatusSuccess({ status });
            } else {
              return locationActions.updateLocationStatusFailure({
                error: res.error!,
              });
            }
          }),
          catchError(() => of(locationActions.updateLocationStatusFailure({})))
        )
      )
    )
  );

  updateLocationStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(locationActions.updateLocationStatusSuccess),
        tap(() =>
          this.messageService.successTranslate(
            'locations.overview.update-success'
          )
        )
      ),
    { dispatch: false }
  );

  updateLocationStatusFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(locationActions.updateLocationStatusFailure),
        tap(() =>
          this.messageService.errorTranslate(
            'locations.overview.update-failed'
          )
        )
      ),
    { dispatch: false }
  );

  showErrorForLocations$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          locationActions.loadLocationListFailure,
          locationActions.saveLocationFailure
        ),
        tap((err) => this.messageService.error(err.error))
      ),
    { dispatch: false }
  );

  filterUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.updateLocationFilter),
      map((_) => locationActions.updateLocationFilterSuccess())
    )
  );

  changedPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.changePagination),
      map((_) => locationActions.changePaginationSuccessful())
    )
  );
}
